import type { FlagKey } from '@gozoki/feature-flags'
import { useRouter } from '@tanstack/react-router'
import { useContext, type ReactNode } from 'react'
import { useAbility, type PermissionTuple } from '~/utils/ability'
import { AuthContext } from '~/utils/context/auth.context'
import {
  PermissionDeniedTooltip,
  useRbacV2Enabled,
  type PermissionRequirement,
  type RouteTo,
} from '~/utils/rbac'
import { resolveTabAuthorization, type TabGuard } from '~/utils/tab-authorization'

export interface GuardedTab<TValue extends string> extends TabGuard {
  value: TValue
  label: ReactNode
  /** Route cible de l'onglet — son `staticData.permission` sert de source par défaut. */
  to?: RouteTo
  /**
   * Flag de l'onglet : éteint, sa barre le masque et la palette ⌘K ne le propose pas. Lu par
   * `useFeatureFlagEvaluator` des deux côtés ; `useAuthorizedTabs` ne le lit pas (droits seuls).
   */
  flagKey?: FlagKey
}

export interface ResolvedTab<TValue extends string> extends GuardedTab<TValue> {
  authorized: boolean
  missingPermissions: PermissionTuple[]
  /** Prêt à poser dans un `Tooltip` ; `undefined` quand l'onglet est accessible. */
  tooltip: ReactNode | undefined
}

/**
 * Résout les droits de chaque onglet. Source par défaut : le `staticData.permission` de la route
 * cible — la permission n'est ainsi déclarée qu'à un seul endroit, la route.
 *
 * Règle produit : un onglet interdit est **désactivé** (avec tooltip), jamais masqué. Le hook
 * renvoie donc tous les onglets, enrichis de leur état d'autorisation.
 */
export const useAuthorizedTabs = <TValue extends string>(
  tabs: GuardedTab<TValue>[],
): ResolvedTab<TValue>[] => {
  const ability = useAbility()
  const router = useRouter()
  const { hasRights } = useContext(AuthContext)
  const v2Enabled = useRbacV2Enabled()

  // Lecture directe du registre de routes : `useRouteStaticData` est un hook et ne peut pas être
  // appelé dans le `map` ci-dessous.
  const routePermission = (to: RouteTo): PermissionRequirement | undefined => {
    // @ts-expect-error — `routesByPath` n'est pas typé côté public.
    const route = router.routesByPath[to] ?? router.routesByPath[`${to}/`]
    return route?.options?.staticData?.permission
  }

  return tabs.map((tab) => {
    const permission = tab.permission ?? (tab.to ? routePermission(tab.to) : undefined)
    // Règle pure partagée avec la palette ⌘K (`tab-authorization.ts`) : flag OFF, seul le bitmask
    // legacy discrimine ; flag ON, un onglet sans permission v2 déclarée n'est jamais ouvert par un
    // bit legacy.
    const { authorized, missingPermissions } = resolveTabAuthorization(
      { permission, legacyRights: tab.legacyRights },
      { ability, v2Enabled, hasRights },
    )

    return {
      ...tab,
      authorized,
      missingPermissions,
      tooltip: authorized ? undefined : (
        <PermissionDeniedTooltip missingPermissions={missingPermissions} />
      ),
    }
  })
}

/** Première valeur autorisée — pour rediriger quand l'onglet actif devient interdit. */
export const useFirstAuthorizedTab = <TValue extends string>(
  tabs: ResolvedTab<TValue>[],
): TValue | undefined => tabs.find((tab) => tab.authorized)?.value
